import { useEffect, useRef, useState } from "react";
import EditorJS from "@editorjs/editorjs";
import Header from "@editorjs/header";
import Paragraph from "@editorjs/paragraph";
import List from "@editorjs/list";
import Quote from "@editorjs/quote";
import CallToAction from "../editorjs/CallToAction";
import ImageBlock from "../editorjs/ImageBlock";
import YoutubeEmbed from "../editorjs/YoutubeEmbed";
import CodeBlock from "../editorjs/CodeBlock";
import ModelBlock from "../editorjs/ModelBlock";
import ClearFormatting from "../editorjs/ClearFormatting";
import GithubGist from "editorjs-github-gist-plugin";
import Delimiter from "@editorjs/delimiter";
import { loadModelViewer } from "../utils/loadModelViewer";
import MonoButton from "./MonoButton";

import ReactMarkdown from "react-markdown";

// Import Generate Markdown SEO JSON 
import getPostBlockingSchema from "../utils/getPostBlockingSchema";
import useDynamicMeta from "../hooks/useDynamicMeta";

// 🧠 Slug Utility
const generateSlug = (input = "") =>
  input
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/g, "-")
    .replace(/(^-|-$)/g, "");

// 🧹 Content Cleanup
function cleanEditorJsBlocks(data) {
  const cleanText = (text) =>
    (typeof text === "string" ? text : "")
      .replace(/<[^>]+>/g, "")
      .replace(/[*#]/g, "")
      .replace(/&gt;/g, ">")
      .replace(/&lt;/g, "<")
      .replace(/&amp;/g, "&")
      .replace(/&nbsp;/g, " ")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&mdash;/g, "—")
      .replace(/&ndash;/g, "–");

  return data.map((block) => {
    const cleaned = { ...block };
    if (cleaned.data?.text) cleaned.data.text = cleanText(cleaned.data.text);
    if (Array.isArray(cleaned.data?.items)) {
      cleaned.data.items = cleaned.data.items.map((item) =>
        typeof item === "string"
          ? cleanText(item)
          : item?.content
          ? { ...item, content: cleanText(item.content) }
          : item
      );
    }
    return cleaned;
  });
}

// 🧠 Plaintext & Markdown Generator
function getPlainTextAndMarkdown(data) {
  const blocks = data?.blocks || [];
  const plainArr = [];
  const mdArr = [];

  blocks.forEach(({ type, data }) => {
    let txt = "";
    let md = "";

    switch (type) {
      case "header":
        txt = data.text || "";
        md = `## ${data.text || ""}`;
        break;
      case "paragraph":
        txt = data.text || "";
        md = data.text || "";
        break;
      case "quote":
        txt = data.text || "";
        md = `> ${data.text || ""}`;
        break;
      case "list":
        const items = data.items || [];
        const flat = items.map((i) =>
          typeof i === "string" ? i : i.content || ""
        );
        txt = flat.join("\n");
        md = flat.map((i) => `- ${i}`).join("\n");
        break;
      case "code":
        txt = data.content || "";
        md = `\`\`\`${data.language || ""}\n${data.content || ""}\n\`\`\``;
        break;
      case "delimiter":
        txt = "---";
        md = "---";
        break;
      case "image":
        const alt = data.alt?.trim() || data.caption?.trim() || "Bild";
        const url = data.file?.url || data.url || data.src || "";
        if (url) {
          txt = `[Bild: ${alt}] ${url}`;
          md = `![${alt}](${url})`;
        }
        break;
      case "youtube":
        txt = data.url ? `[YouTube: ${data.url}]` : "";
        md = data.url ? `🔗 ${data.url}` : "";
        break;
      case "gist":
        txt = data.url ? `[GitHub Gist: ${data.url}]` : "";
        md = data.url ? `🔗 ${data.url}` : "";
        break;
      case "cta":
        txt = data.content || "";
        md = `**${data.content || ""}**`;
        break;
      case "model3d":
        txt = data.title || "3D-Modell";
        md = `📦 ${txt}`;
        break;
      default:
        break;
    }

    if (txt) plainArr.push(txt);
    if (md) mdArr.push(md);
  });

  return {
    plain: plainArr.join("\n\n"),
    markdown: mdArr.join("\n\n"),
  };
}

// 🔽 File Download Utility
function downloadFile(data, type, filename) {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/* ===========================
   Markdown → EditorJS Blocks
   =========================== */
function mdToEditorBlocks(md) {
  const lines = md.split("\n");
  const blocks = [];
  let listBuffer = [];

  const flushList = () => {
    if (listBuffer.length > 0) {
      blocks.push({
        type: "list",
        data: { style: "unordered", items: [...listBuffer] },
      });
      listBuffer = [];
    }
  };

  let paragraphBuffer = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length > 0) {
      // join with double-newline behavior: keep paragraphs as single block
      blocks.push({
        type: "paragraph",
        data: { text: paragraphBuffer.join("\n") },
      });
      paragraphBuffer = [];
    }
  };

  for (let rawLine of lines) {
    const line = rawLine.replace(/\r$/, ""); // windows safe
    if (/^#{1,6}\s+/.test(line)) {
      flushList();
      flushParagraph();
      const level = Math.min(6, line.match(/^#{1,6}/)[0].length);
      const text = line.replace(/^#{1,6}\s+/, "");
      // Use header level 2/3 for compatibility if level 1 appears, map H1 -> H2
      const headerLevel = level === 1 ? 2 : Math.min(3, level);
      blocks.push({
        type: "header",
        data: { level: headerLevel, text },
      });
      continue;
    }

    if (/^>\s+/.test(line)) {
      flushList();
      flushParagraph();
      blocks.push({
        type: "quote",
        data: { text: line.replace(/^>\s+/, "") },
      });
      continue;
    }

    if (/^-\s+/.test(line) || /^\*\s+/.test(line) || /^\d+\.\s+/.test(line)) {
      // list item
      const item = line.replace(/^(-|\*|\d+\.)\s+/, "");
      listBuffer.push(item);
      continue;
    }

    const imageMatch = line.match(/!\[(.*?)\]\((.*?)\)/);
    if (imageMatch) {
      flushList();
      flushParagraph();
      blocks.push({
        type: "image",
        data: {
          caption: imageMatch[1] || "",
          file: { url: imageMatch[2] || "" },
        },
      });
      continue;
    }

    // CODEBLOCK erkennen — robust
const codeFenceStart = line.match(/^```([\w-]*)/);
if (codeFenceStart) {
  flushList();
  flushParagraph();

  const lang = codeFenceStart[1] || "text";
  let codeLines = [];
  let i = lines.indexOf(rawLine) + 1;

  // alle Zeilen einsammeln bis closing fence
  while (i < lines.length && !/^```/.test(lines[i])) {
    codeLines.push(lines[i]);
    lines[i] = ""; // verbrauchen
    i++;
  }

  // closing fence ebenfalls verbrauchen
  if (i < lines.length) {
    lines[i] = "";
  }

  blocks.push({
    type: "code",
    data: {
      language: lang,
      content: codeLines.join("\n")
    }
  });

  continue;
}


    const codeFenceMatch = line.match(/^```(.*)$/);
    if (codeFenceMatch) {
      // collect until closing ```
      flushList();
      flushParagraph();
      const lang = codeFenceMatch[1] || "";
      let codeLines = [];
      // consume following lines until ```
      // find index of the current line
      const startIndex = lines.indexOf(rawLine);
      // We'll manually iterate from the current position (but we can't mutate the for loop index easily),
      // so instead handle code fences by scanning forward here:
      let idx = lines.indexOf(rawLine) + 1;
      while (idx < lines.length && !/^```/.test(lines[idx])) {
        codeLines.push(lines[idx]);
        idx++;
      }
      // Remove consumed lines from the array by replacing them with empty strings so the outer loop skips them.
      for (let j = lines.indexOf(rawLine) + 1; j <= idx; j++) {
        lines[j] = "";
      }
      blocks.push({
        type: "code",
        data: { language: lang, content: codeLines.join("\n") },
      });
      continue;
    }

    if (line.trim() === "") {
      // blank line separates blocks
      flushList();
      flushParagraph();
      continue;
    }

    // default: accumulate into paragraph buffer
    paragraphBuffer.push(line);
  }

  flushList();
  flushParagraph();

  return blocks;
}

/* ===========================
   Main Component
   =========================== */
export default function SmartZenEditor() {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [date, setDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [image, setImage] = useState("");
  const [editorData, setEditorData] = useState(null);
  const [availableArticles, setAvailableArticles] = useState([]);
  const [markdown, setMarkdown] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const ejInstance = useRef(null);
  const slug = generateSlug(title);

  const mdUploadRef = useRef(null);

  useDynamicMeta({
    title,
    description: subtitle,
    image,
    schemaJson: getPostBlockingSchema({
      title,
      subtitle,
      date,
      image,
      markdown,
      slug,
    }),
  });

  // Load article list
  useEffect(() => {
    fetch("https://www.denisbitter.de/stage01/api/zens.php")
      .then((res) => res.json())
      .then(setAvailableArticles)
      .catch(console.error);
  }, []);

  // Init EditorJS
  useEffect(() => {
    loadModelViewer();

    const timeout = setTimeout(() => {
      ejInstance.current = new EditorJS({
        holder: "editorjs",
        autofocus: true,
        tools: {
          delimiter: Delimiter,
          header: {
            class: Header,
            inlineToolbar: ["link", "bold", "italic", "clearFormatting"],
            config: { levels: [2, 3, 4], defaultLevel: 2 },
          },
          paragraph: {
            class: Paragraph,
            inlineToolbar: ["link", "bold", "italic", "clearFormatting"],
          },
          list: List,
          quote: Quote,
          cta: CallToAction,
          image: ImageBlock,
          youtube: YoutubeEmbed,
          code: CodeBlock,
          model3d: { class: ModelBlock, toolbox: ModelBlock.toolbox },
          clearFormatting: ClearFormatting,
          gist: {
            class: GithubGist,
            config: {
              endpoint: "https://api.github.com/gists",
            },
          },
        },
        onReady: () => console.log("✅ Editor.js ready"),
      });
    }, 0);

    return () => {
      clearTimeout(timeout);
      if (ejInstance.current && typeof ejInstance.current.destroy === "function") {
        ejInstance.current.destroy();
        ejInstance.current = null;
      }
    };
  }, []);

  // Save handler (existing)
  const handleSave = async () => {
    try {
      const output = await ejInstance.current.save();
      const cleaned = cleanEditorJsBlocks(output.blocks);
      setEditorData({ blocks: cleaned });

      const { markdown: md } = getPlainTextAndMarkdown({ blocks: cleaned });
      setMarkdown(md);

      await fetch("https://www.denisbitter.de/stage01/api/save_zens.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug,
          title,
          subtitle,
          date,
          image: image || `https://www.denisbitter.de/images/${slug}.jpg`,
          blocks: cleaned,
        }),
      });
      alert("✅ Artikel gespeichert!");
    } catch (err) {
      console.error(err);
      alert("Fehler beim Speichern. Schau in die Konsole.");
    }
  };

  const downloadPlain = () => {
    const plain = getPlainTextAndMarkdown(editorData).plain;
    downloadFile(plain, "text/plain", `${slug || "post"}-klartext.txt`);
  };

  const downloadMd = () => {
    downloadFile(markdown, "text/markdown", `${slug || "post"}-post.md`);
  };

  // Load JSON article
  const loadJson = async (slugValue) => {
    if (!slugValue) return;
    const res = await fetch(
      `https://www.denisbitter.de/stage01/api/zens.php?slug=${slugValue}`
    );
    const json = await res.json();
    const raw = json.blocks || json.content?.blocks || [];
    const blocks = raw.map((b) =>
      b.type === "model" ? { ...b, type: "model3d" } : b
    );
    if (ejInstance.current && typeof ejInstance.current.render === "function") {
      ejInstance.current.render({ blocks });
    }
    setEditorData({ blocks });

    const { markdown: md } = getPlainTextAndMarkdown({ blocks });
    setMarkdown(md);

    setTitle(json.title || "");
    setSubtitle(json.subtitle || "");
    setDate(json.date || new Date().toISOString().slice(0, 10));
    setImage(json.image || "");
  };
  
// Refresh Reload Button 

const refreshArticleList = () => {
  fetch("https://www.denisbitter.de/stage01/api/zens.php")
    .then((res) => res.json())
    .then(setAvailableArticles)
    .catch(console.error);
};
// 🔥 DELETE Article Handler
const handleDelete = async () => {
  if (!slug) return alert("Kein Artikel zum Löschen ausgewählt.");
  setShowDeleteConfirm(true);
};

const confirmDelete = async () => {
  setShowDeleteConfirm(false);
  try {
    const res = await fetch(
      "https://www.denisbitter.de/stage01/api/delete_zens.php",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ slug }),
      }
    );
    const json = await res.json();
    if (json.success) {
      alert(`✅ Artikel "${title}" gelöscht!`);
      setTitle("");
      setSubtitle("");
      setDate(new Date().toISOString().slice(0, 10));
      setImage("");
      setEditorData(null);
      setMarkdown("");
      refreshArticleList();
    } else {
      alert("Fehler beim Löschen: " + (json.error || "Unbekannt"));
    }
  } catch (err) {
    console.error(err);
    alert("Fehler beim Löschen des Artikels. Schau in die Konsole.");
  }
};

const cancelDelete = () => {
  setShowDeleteConfirm(false);
};

  /* =======================================
     NEW: Handle Markdown Upload & Save
     - reads .md file, converts to blocks
     - extracts title + image
     - saves to server via existing API
     ======================================= */
  const handleMarkdownUploadAndSave = async (e) => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      const text = await file.text();
      setMarkdown(text);

      // convert to blocks and clean
      const rawBlocks = mdToEditorBlocks(text);
      const cleanedBlocks = cleanEditorJsBlocks(rawBlocks);

      // render into editor
      if (ejInstance.current && typeof ejInstance.current.render === "function") {
        ejInstance.current.render({ blocks: cleanedBlocks });
      }
      setEditorData({ blocks: cleanedBlocks });

      // get title: first H1/H2 or first non-empty line
      let newTitle = "";
      const h1 = text.match(/^#\s+(.*)/m);
      const h2 = text.match(/^##\s+(.*)/m);
      if (h1) newTitle = h1[1].trim();
      else if (h2) newTitle = h2[1].trim();
      else {
        const firstNonEmpty = text.split("\n").find((l) => l.trim() !== "");
        newTitle = (firstNonEmpty || "Neuer Zen").replace(/^#+\s*/, "").slice(0, 120);
      }
      setTitle(newTitle);

      // subtitle: try frontmatter style or next line (simple heuristic)
      let newSubtitle = "";
      const subtitleMatch = text.match(/^-{3}[\s\S]*?title:.*?subtitle:\s*(.*)/m);
      if (subtitleMatch) newSubtitle = subtitleMatch[1].trim();
      setSubtitle(newSubtitle);

      // slug
      const newSlug = generateSlug(newTitle);

      // image: first markdown image or fallback
      const imgMatch = text.match(/!\[.*?\]\((.*?)\)/);
      const imgUrl = imgMatch?.[1] || `https://www.denisbitter.de/images/${newSlug}.jpg`;
      setImage(imgUrl);

      // date: today
      const newDate = new Date().toISOString().slice(0, 10);
      setDate(newDate);

      // send to server
      await fetch("https://www.denisbitter.de/stage01/api/save_zens.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          slug: newSlug,
          title: newTitle,
          subtitle: newSubtitle || "",
          date: newDate,
          image: imgUrl,
          blocks: cleanedBlocks,
        }),
      });

      alert("🚀 Markdown geladen & als ZenPost gespeichert!");
      // reset file input value so same file can be chosen again if needed
      e.target.value = "";
    } catch (err) {
      console.error(err);
      alert("Fehler beim Importieren / Speichern der Markdown-Datei.");
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-6 p-6">
      {/* LEFT: Editor & Save */}
      <div className="space-y-4">
        <h2 className="text-xs">✍️ ZenSmartEditor</h2>

             <MonoButton
            className="text-xs w-1/2"
            onClick={() => mdUploadRef.current && mdUploadRef.current.click()}
          >
            📤 Markdown laden & speichern
          </MonoButton>


            <MonoButton
      className="text-xs bg-red-500 w-1/2 hover:bg-red-600"
      onClick={handleDelete}
    >
      🗑️ Artikel löschen
    </MonoButton>

      <MonoButton className="text-xs w-full" onClick={refreshArticleList}>
    🔄 Refresh
  </MonoButton>

<div className="flex items-center gap-2 mb-2">
  <select
    onChange={(e) => e.target.value && loadJson(e.target.value)}
    className="border p-1 w-full text-xs"
  >
    <option value="">Zen wählen…</option>
    {availableArticles.map((a) => (
      <option key={a} value={a}>{a}</option>
    ))}
  </select>


</div>


        

        <input
          placeholder="Titel"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="border p-1 w-full mb-1 text-xs"
        />
        <input
          placeholder="Untertitel"
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          className="border p-1 w-full mb-1 text-xs"
        />
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="border p-1 w-full mb-1 text-xs"
        />
        <input
          placeholder="Bild-URL"
          value={image}
          onChange={(e) => setImage(e.target.value)}
          className="border p-1 w-full mb-2 text-xs"
        />

        <div id="editorjs" className="min-h-[300px] border mb-2 text-xs"></div>

        <div className="flex gap-2">
          <MonoButton className="text-xs" onClick={handleSave}>
            💾 Save & Generate Markdown
          </MonoButton>

     
          <input
            type="file"
            accept=".md,text/markdown"
            ref={mdUploadRef}
            onChange={handleMarkdownUploadAndSave}
            className="hidden"
          />
        </div>
      </div>

  {/* RIGHT: Preview & Downloads */}
  <div>
    <h2 className="text-xs">📄 Live Markdown Preview</h2>
    <div className="border p-4 mb-2 max-h-[500px] overflow-auto text-xs">
      <ReactMarkdown>{markdown}</ReactMarkdown>
    </div>

    {showDeleteConfirm && (
      <div className="border border-red-500 bg-red-50 p-3 mb-2 text-xs rounded">
        <p className="mb-2">Willst du den Artikel "{title}" wirklich löschen?</p>
        <div className="flex gap-2">
          <MonoButton
            className="text-xs bg-red-500 hover:bg-red-600"
            onClick={confirmDelete}
          >
            Ja, löschen
          </MonoButton>
          <MonoButton
            className="text-xs bg-gray-400 hover:bg-gray-500"
            onClick={cancelDelete}
          >
            Abbrechen
          </MonoButton>
        </div>
      </div>
    )}




    <div className="flex gap-2 mb-4">
      <MonoButton onClick={downloadPlain} className="text-xs">
        📥 Klartext herunterladen
      </MonoButton>
      <MonoButton className="text-xs" onClick={downloadMd}>
        📥 Markdown herunterladen
      </MonoButton>
    </div>

    <h4 className="mt-4 text-xs">🔍 JSON Debug Output</h4>
  <pre className="border p-2 max-h-[200px] overflow-auto text-xs">
    {editorData
      ? JSON.stringify(cleanEditorJsBlocks(editorData.blocks), null, 2)
      : "// Noch nichts geladen"}
  </pre>
</div>
</div>
  );
}
